import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const History = () => {
  // State management
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0
  });
  
  // Derived state
  const { currentPage, lastPage, perPage } = pagination;

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [updatingHistoryId, setUpdatingHistoryId] = useState(null);
  const token = localStorage.getItem('token');
  
  // Close detail modal and clean up
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedHistory(null);
    setHistoryDetail(null);
  };

  // Status tabs configuration
  const statusTabs = [
    { key: 'all', label: 'All', count: 0 },
    { key: 'pending', label: 'Pending', count: 0 },
    { key: 'approved', label: 'Approved', count: 0 },
    { key: 'rejected', label: 'Rejected', count: 0 }
  ];

  // API Functions
  const fetchHistoryList = React.useCallback(async (page = 1, status = '', search = '') => {
    setLoading(true);
    try {
      setError(null);
      
      const params = new URLSearchParams({
        page,
        limit: perPage,
        ...(status && { status }),
        ...(search && { search: search.trim() })
      });

      const url = `${baseUrl}/history/list?${params.toString()}`;
      console.log('API URL:', url);

      const res = await fetch(url, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const result = await res.json();
      console.log('API Response:', result);
      
      if (result.status && result.data) {
        const items = Array.isArray(result.data) ? result.data : [];
        
        setPagination(prev => ({
          ...prev,
          currentPage: result.pagination?.currentPage || 1,
          lastPage: result.pagination?.lastPage || 1,
          perPage: result.pagination?.perPage || perPage,
          total: result.pagination?.total || 0
        }));
        
        setHistoryData(items);
      } else {
        setHistoryData([]);
        setPagination(prev => ({
          ...prev,
          currentPage: 1,
          lastPage: 1,
          total: 0
        }));
        
        if (result.message) {
          console.log('API message:', result.message);
          toast.error(result.message);
        }
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching history:', err);
      // Reset state on error
      setHistoryData([]);
      setPagination(prev => ({
        ...prev,
        currentPage: 1,
        lastPage: 1,
        total: 0
      }));
    } finally {
      setLoading(false);
    }
  }, [perPage, token]);

  const fetchHistoryDetail = async (id) => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/history/detail/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const result = await res.json();
      if (result.status && result.data) {
        setHistoryDetail(result.data);
        setShowDetailModal(true);
      } else {
        throw new Error(result.message || 'Failed to fetch history details');
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to fetch history details');
      console.error('Error fetching history detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateHistoryStatus = async (id, newStatus, reason = '') => {
    try {
      setStatusUpdateLoading(true);
      const requestBody = { status: newStatus };
      if (newStatus === 'rejected' && reason) {
        requestBody.reason = reason;
      }
      
      const res = await fetch(`${baseUrl}/history/update-status/${id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Update failed');
      
      // Refresh the list after update, maintaining current pagination
      await fetchHistoryList(pagination.currentPage, activeTab, debouncedSearchTerm);
      toast.success('Status updated successfully');
      
      // Also refresh the detail modal if it's open for this item
      if (historyDetail && historyDetail.id === id) {
        await fetchHistoryDetail(id);
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to update status');
      console.error('Error updating status:', err);
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  // Effects
  // Fetch data when pagination or filters change
  useEffect(() => {
    if (!token) return;
    
    const fetchData = async () => {
      await fetchHistoryList(
        pagination.currentPage,
        activeTab === 'all' ? '' : activeTab,
        searchTerm
      );
    };
    
    fetchData();
  }, [pagination.currentPage, pagination.perPage, activeTab, searchTerm, token, fetchHistoryList]);
  
  // Handle page changes
  const handlePageChange = (page) => {
    if (page >= 1 && page <= lastPage && page !== currentPage) {
      setPagination(prev => ({
        ...prev,
        currentPage: page
      }));
    }
  };

  // Handle items per page change
  // const handleItemsPerPageChange = (newPerPage) => {
  //   if (newPerPage !== perPage) {
  //     setPagination(prev => ({
  //       ...prev,
  //       perPage: newPerPage,
  //       currentPage: 1
  //     }));
  //   }
  // };
  
  // Navigation handlers
  const goToFirstPage = () => handlePageChange(1);
  const goToLastPage = () => handlePageChange(lastPage);
  // const goToNextPage = () => handlePageChange(Math.min(currentPage + 1, lastPage));
  // const goToPrevPage = () => handlePageChange(Math.max(currentPage - 1, 1));

  // Event handlers
  const handleTabChange = (tabKey) => {
    if (tabKey !== activeTab) {
      setActiveTab(tabKey);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({
        ...prev,
        currentPage: 1
      }));
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  

  const handleViewDetails = (history) => {
    setSelectedHistory(history);
    fetchHistoryDetail(history.id);
  };

  const handleStatusUpdate = (id, status) => {
    if (status === 'rejected') {
      setUpdatingHistoryId(id);
      setRejectionReason('');
      setShowRejectModal(true);
    } else {
      updateHistoryStatus(id, status);
    }
  };

  const handleRejectConfirm = () => {
    if (rejectionReason.trim()) {
      updateHistoryStatus(updatingHistoryId, 'rejected', rejectionReason.trim());
      setShowRejectModal(false);
      setRejectionReason('');
    } else {
      toast.error('Please enter a reason for rejection');
    }
  };

  // Helper functions
  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-900 text-yellow-200',
      approved: 'bg-green-900 text-green-200',
      rejected: 'bg-red-900 text-red-200'
    };
    return statusClasses[status] || 'bg-gray-700 text-gray-200';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex bg-gray-900 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Navbar />
        
        {/* Rejection Reason Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-white mb-4">Reason for Rejection</h3>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Please provide a reason for rejection..."
              />
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('');
                    // Reset the dropdown to previous value
                    const dropdown = document.querySelector(`select[data-id="${updatingHistoryId}"]`);
                    if (dropdown) {
                      const historyItem = historyData.find(h => h.id === updatingHistoryId);
                      if (historyItem) dropdown.value = historyItem.status;
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-600 hover:bg-gray-500 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                  disabled={!rejectionReason.trim()}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white">History</h1>
            <p className="text-gray-300 mt-2">Manage and view all history records</p>
          </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Search history..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-800">
            <nav className="-mb-px flex space-x-8">
              {statusTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.key
                      ? 'border-white text-white'
                      : 'border-transparent text-gray-400 hover:text-white hover:border-white'
                  }`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                      activeTab === tab.key ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* History Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading history...</p>
            </div>
          ) : historyData.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-4">No history records found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Transaction ID
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Username & User ID
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Game
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Gems
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Receiver Coin
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Created Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-900 divide-y divide-gray-700">
                    {historyData.map((history) => (
                      <tr key={history.id} className="hover:bg-gray-800 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-200">
                          {history.transactionId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-200">
                          {history.username}
                          <div className="text-xs text-gray-400">User ID: {history.userId}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-200">{history.gameName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-200">{history.coin}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-400 truncate max-w-xs">{history.receiverCoin}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(history.status)}`}>
                            {history.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {formatDate(history.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(history)}
                            className="text-blue-400 hover:text-blue-300 mr-4 transition-colors duration-150"
                          >
                            View Details
                          </button>
                          <select
                            data-id={history.id}
                            onChange={(e) => handleStatusUpdate(history.id, e.target.value)}
                            value={history.status}
                            className="text-sm border border-gray-600 rounded px-2 py-1 bg-gray-800 text-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            disabled={statusUpdateLoading || history.status !== 'pending'}
                          >
                            {history.status === 'pending' && (
                              <>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                              </>
                            )}
                            {history.status === 'approved' && (
                              <option value="approved">Approved</option>
                            )}
                            {history.status === 'rejected' && (
                              <option value="rejected">Rejected</option>
                            )}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Enhanced Pagination */}
              <div className="bg-gray-800 px-4 py-3 border-t border-gray-800 sm:px-6">
                {/* Mobile Pagination */}
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage <= 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-300 flex items-center">
                    {pagination.lastPage > 0 ? `Page ${pagination.currentPage} of ${pagination.lastPage}` : 'No pages'}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage >= pagination.lastPage}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>

                {/* Desktop Pagination */}
                <div className="hidden sm:flex sm:items-center sm:justify-between">
                  {/* Results Info and Items Per Page */}
                  <div className="flex items-center space-x-4">
                    <div>
                      <p className="text-sm text-gray-300">
                        {pagination.total > 0 ? (
                          <>
                            Showing <span className="font-medium text-white">{(pagination.currentPage - 1) * pagination.perPage + 1}</span> to{' '}
                            <span className="font-medium text-white">{Math.min(pagination.currentPage * pagination.perPage, pagination.total)}</span> of{' '}
                            <span className="font-medium text-white">{pagination.total}</span> results
                          </>
                        ) : (
                          <span className="text-gray-400">No results found</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Navigation Controls */}
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        {/* First Page Button */}
                        <button
                          onClick={goToFirstPage}
                          disabled={pagination.currentPage <= 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="First Page"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                          </svg>
                        </button>
                        
                        {/* Previous Page Button */}
                        <button
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                          disabled={pagination.currentPage <= 1}
                          className="relative inline-flex items-center px-2 py-2 border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Previous Page"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>

                        {/* Page Numbers - Show All */}
                        {pagination.lastPage > 0 && [...Array(pagination.lastPage)].map((_, index) => {
                          const page = index + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              disabled={false}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === pagination.currentPage
                                  ? 'z-10 bg-blue-600 border-blue-700 text-white'
                                  : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}

                        {/* Next Page Button */}
                        <button
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                          disabled={pagination.currentPage >= pagination.lastPage}
                          className="relative inline-flex items-center px-2 py-2 border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Next Page"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        {/* Last Page Button */}
                        <button
                          onClick={goToLastPage}
                          disabled={pagination.currentPage >= pagination.lastPage}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Last Page"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedHistory && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-6 border w-11/12 md:w-4/5 lg:w-3/5 xl:w-1/2 shadow-2xl rounded-lg bg-white max-h-[90vh] overflow-y-auto">
              <div className="mt-3">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Transaction Details</h3>
                    <p className="text-sm text-gray-500 mt-1">Complete transaction information</p>
                  </div>
                  <button
                    onClick={closeDetailModal}
                    className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Transaction Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Basic Information</h4>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-medium text-gray-600 mb-1">Transaction ID</label>
                      <p className="text-lg font-mono text-blue-600 bg-white px-3 py-2 rounded border">
                        {selectedHistory.transactionId}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Record ID</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">#{selectedHistory.id}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">User Game ID</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">{selectedHistory.userGameId}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeClass(selectedHistory.status)}`}>
                        {selectedHistory.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* User & Game Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">User & Game Details</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Username</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded flex items-center">
                        <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {selectedHistory.username}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded flex items-center">
                        <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {selectedHistory.email}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Game Name</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded flex items-center">
                        <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M15 14h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {selectedHistory.gameName}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">User ID</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">{selectedHistory.userId}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Game ID</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">{selectedHistory.gameId}</p>
                    </div>
                  </div>
                </div>

                {/* Transaction Values */}
                {selectedHistory.reason !== null && (
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">Rejected Reason</h4>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">{selectedHistory.reason}</p>
                </div>
                )}

                {/* Transaction Values */}
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">Transaction Values</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                      <label className="block text-sm font-medium text-blue-700 mb-1">Gems (Coin)</label>
                      <p className="text-2xl font-bold text-blue-800 flex items-center">
                        <svg className="h-6 w-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                        {selectedHistory.coin}
                      </p>
                    </div>
                    
                    <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                      <label className="block text-sm font-medium text-green-700 mb-1">Receiver Coin</label>
                      <p className="text-2xl font-bold text-green-800 flex items-center">
                        <svg className="h-6 w-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                        {selectedHistory.receiverCoin}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">Timestamps</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Created Date</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded flex items-center">
                        <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatDate(selectedHistory.createdAt)}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Last Updated</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded flex items-center">
                        <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {selectedHistory.updatedAt ? formatDate(selectedHistory.updatedAt) : 'Not updated'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="mt-8 flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={closeDetailModal}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 flex items-center"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default History;